import getColumnConstraintWithRowData from 'notebook/structured-tables/columns/getColumnConstraint';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {RowModel} from 'notebook/tables/state/RowState';
import {debuggableSelectorCreator, deepSelectorCreator} from 'notebook/tables/state/SelectorCreators';
import {Cell, ColumnConstraint, isTextEditableType, RowData} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BenchlingState} from 'redux-stores/reduceApp';

/**
 * This holds volatile state, or state that frequently changes due to evaluation or renders.
 * This should contain pieces of volatile state from the RowState that are only relevant to this single cell.
 *
 * Everything inside here will be deep-compare memoized.
 */
export interface VolatileCellState {
  cell: Cell;
  evaluatedCell: EvaluatedCell;
}

/**
 * This model is what is passed down to the components.
 * This can contain data that is derived from the RowModel, TableModel, or TableState.
 */
export interface CellModel extends VolatileCellState {
  rowData: RowData | null;
  columnConstraint: ColumnConstraint | null;
}

const createVolatileCellStateSelector = (
  rowSelector: (state: BenchlingState) => RowModel,
  columnIndex: number
): ((state: BenchlingState) => VolatileCellState) =>
  deepSelectorCreator(
    [(state) => rowSelector(state).row[columnIndex], (state) => rowSelector(state).evaluatedRow[columnIndex]],
    (cell, evaluatedCell) => ({cell, evaluatedCell})
  );

export const createCellModelSelector = (
  rowSelector: (state: BenchlingState) => RowModel,
  columnIndex: number
): ((state: BenchlingState) => CellModel) =>
  debuggableSelectorCreator(
    [
      // volatileState
      createVolatileCellStateSelector(rowSelector, columnIndex),
      // rowData
      (state) => rowSelector(state).rowData,
      // columnConstraint
      (state) => rowSelector(state).columnConstraints[columnIndex],
      // isTombstoned
      (state) => rowSelector(state).rowProperties.isTombstoned,
    ],
    (volatileState, rowData, columnConstraint, isTombstoned): CellModel => ({
      cell: {
        ...volatileState.cell,
        text:
          volatileState.cell.text ||
          (volatileState.cell.typedValue != null && isTextEditableType(volatileState.cell.typedValue)
            ? formatTypedValue(volatileState.cell.typedValue)
            : ''),
        // grayOutContents is only set for StructuredTables
        ...(isTombstoned && {grayOutContents: true}),
      },
      evaluatedCell: volatileState.evaluatedCell,
      rowData,
      columnConstraint: getColumnConstraintWithRowData(columnConstraint, rowData),
    })
  );
